/**
 * Empty Buckets State Component
 * 
 * Displays when a user has no content buckets, encouraging them to create their first bucket.
 * 
 * Features:
 * - Visual illustration
 * - Call-to-action buttons
 * - Benefits explanation
 * - Mobile-responsive design
 * 
 * @author AI Assistant
 * @created 2024
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as TemplateIcon,
  Folder as FolderIcon,
  TrendingUp as TrendingIcon,
  Schedule as ScheduleIcon,
  Lightbulb as IdeaIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6, 3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 2)
  }
}));

const IllustrationBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .folder-icon': {
    fontSize: '4rem',
    color: theme.palette.grey[300],
    marginBottom: theme.spacing(2)
  }
}));

const BenefitsGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4)
}));

const BenefitCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)'
  }
}));

const BenefitIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    fontSize: '2.5rem'
  }
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    '& button': {
      width: '100%',
      maxWidth: 280
    }
  }
}));

const EmptyBucketsState = ({ onCreateNew, onCreateFromTemplate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    {
      icon: <TrendingIcon />,
      title: 'Better Organization',
      description: 'Keep your content organized by theme and purpose'
    },
    {
      icon: <ScheduleIcon />,
      title: 'Consistent Posting',
      description: 'Set frequency targets for each content type'
    },
    {
      icon: <IdeaIcon />,
      title: 'Content Ideas',
      description: 'Get inspired with curated content suggestions'
    }
  ];

  return (
    <EmptyStateContainer>
      {/* Illustration */}
      <IllustrationBox>
        <FolderIcon className="folder-icon" />
        <Typography variant="h4" component="h2" gutterBottom fontWeight={600}>
          No Content Buckets Yet
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ maxWidth: 500, mx: 'auto', lineHeight: 1.6 }}
        >
          Content buckets help you organize your posts into thematic categories, 
          maintain content variety, and plan your social media strategy more effectively.
        </Typography>
      </IllustrationBox>

      {/* Benefits */}
      <BenefitsGrid container spacing={3} justifyContent="center">
        {benefits.map((benefit, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <BenefitCard elevation={1}>
              <BenefitIcon>
                {benefit.icon}
              </BenefitIcon>
              <Typography variant="h6" component="h3" gutterBottom fontWeight={600}>
                {benefit.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {benefit.description}
              </Typography>
            </BenefitCard>
          </Grid>
        ))}
      </BenefitsGrid>

      {/* Action Buttons */}
      <ActionButtonsContainer>
        <Button
          variant="outlined"
          size="large"
          startIcon={<TemplateIcon />}
          onClick={onCreateFromTemplate}
          sx={{ 
            minWidth: 200,
            py: 1.5,
            fontSize: '1rem'
          }}
        >
          Use Template
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={onCreateNew}
          sx={{ 
            minWidth: 200,
            py: 1.5,
            fontSize: '1rem'
          }}
        >
          Create First Bucket
        </Button>
      </ActionButtonsContainer>

      {/* Additional Help Text */}
      <Box mt={4}>
        <Typography variant="body2" color="text.secondary">
          💡 <strong>Tip:</strong> Start with an industry template to get pre-configured buckets, 
          or create your own custom buckets from scratch.
        </Typography>
      </Box>
    </EmptyStateContainer>
  );
};

export default EmptyBucketsState; 